/**
 * Keeping the screen awake.
 *
 * A phone that sleeps in the second quarter is a scorekeeper unlocking it
 * between every possession, and the scorebook is exactly the screen that sits
 * untouched for ninety seconds and then needs a tap immediately.
 *
 * The web API drops the lock on its own whenever the page is hidden, and does
 * NOT restore it on return, so a caller has to re-request after a background.
 * That is a real difference from native and it is why `request` is idempotent
 * and safe to call again.
 */

import { browserNavigator, type Adapter } from './host.js';

export interface WakeLock extends Adapter {
  /** Idempotent. Returns whether the screen is held awake now. */
  readonly request: () => Promise<boolean>;
  readonly release: () => Promise<void>;
  readonly isHeld: () => boolean;
}

let sentinel: WakeLockSentinel | null = null;
/**
 * The in-flight request, so two callers cannot both acquire.
 *
 * `request()` checked `sentinel` and then awaited, so two callers entering
 * before either await resolved both saw null, both asked for a lock, and one of
 * the two sentinels became unreachable and could never be released. That is the
 * expected call pattern rather than an exotic one: the web API drops the lock
 * on every background, so two components both re-requesting on
 * `visibilitychange` is exactly what happens.
 */
let pending: Promise<boolean> | null = null;
/**
 * Bumped by every `release`. A request still in flight across a release (the
 * scorebook unmounting just after a return to the foreground re-requested)
 * was granted to nobody: it hands its lock straight back instead of leaking
 * one that nothing can release.
 */
let generation = 0;

interface NavigatorWakeLock {
  request: (type: 'screen') => Promise<WakeLockSentinel>;
}

function api(): NavigatorWakeLock | null {
  const nav = browserNavigator();
  return nav !== null && 'wakeLock' in nav ? nav.wakeLock : null;
}

export const wakeLock: WakeLock = {
  isSupported: () => api() !== null,

  request: async () => {
    const lock = api();
    if (lock === null) return false;
    if (sentinel !== null && !sentinel.released) return true;
    // Join the in-flight request rather than starting a second one.
    if (pending !== null) return await pending;

    const asked = generation;
    pending = (async () => {
      try {
        const acquired = await lock.request('screen');
        if (asked !== generation) {
          await acquired.release().catch(() => {});
          return false;
        }
        sentinel = acquired;
        acquired.addEventListener('release', () => {
          // Compare identity. This used to null the module variable
          // unconditionally, so an OLD sentinel's release event cleared a NEWER
          // one: `isHeld()` then reported false while a lock was genuinely held
          // and locks accumulated for the life of the page.
          if (sentinel === acquired) sentinel = null;
        });
        return true;
      } catch {
        // Denied, or the document is not visible. Not an error worth
        // surfacing: the screen sleeping is a papercut, not a lost event.
        return false;
      } finally {
        // A release already let go of this one, and a newer request may hold
        // `pending` now.
        if (asked === generation) pending = null;
      }
    })();

    return await pending;
  },

  release: async () => {
    generation += 1;
    pending = null;
    const held = sentinel;
    sentinel = null;
    if (held === null || held.released) return;
    try {
      await held.release();
    } catch {
      // Already gone. Nothing to do and nothing to say.
    }
  },

  isHeld: () => sentinel !== null && !sentinel.released,
};
